import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';

/** Add loading="lazy" and decoding="async" to img tags to reduce bandwidth and avoid blocking. */
function addImageOptimizations(html: string): string {
  return html.replace(/<img\s+/gi, '<img loading="lazy" decoding="async" ');
}

import { useAsync } from 'react-use';
import { decodeBase64, flattenObject } from '@/shared/js';
import { addComma } from '@/shared/number';
import { cn } from '@/shared/react';
import { filterXss } from '@/shared/string';
import { contents } from './contents';

type DocName = keyof typeof contents;

type DocProps = {
  className?: string;
  bind?: Record<string, unknown>;
} & (
  | {
      name: DocName;
    }
  | {
      b64: string;
    }
  | {
      raw: string;
    }
);

export const Doc: FC<DocProps> = ({ className, bind, ...props }) => {
  const {
    i18n: { locale },
  } = useLingui();

  const content = useAsync(async () => {
    let ret = '';
    if ('name' in props) {
      const langs = contents[props.name];
      // Fall back to en while the translation is not pulled yet.
      const importFn = langs?.[locale] ?? langs?.en;
      if (!importFn) return '';
      const file = await importFn();
      ret = file?.html || '';
    } else if ('b64' in props) {
      ret = decodeBase64(props.b64) || '';
    } else if ('raw' in props) {
      ret = props.raw || '';
    }

    const flatObject = flattenObject(bind ?? {});

    Object.entries(flatObject).forEach(([key, val]) => {
      const hasValue =
        typeof val === 'number' ||
        typeof val === 'string' ||
        typeof val === 'boolean';
      if (!hasValue) {
        ret = ret.replaceAll(`{${key}}`, 'N/A');
      } else if (/^-?\d+\.?\d*$/.test(val.toString())) {
        ret = ret.replaceAll(`{${key}}`, addComma(val.toString()));
      } else {
        ret = ret.replaceAll(`{${key}}`, val.toString());
      }
    });

    return ret;
  }, [JSON.stringify(props), locale]);

  return (
    <div
      className={cn(
        (content.loading || content.error) && 'shimmer min-h-36',
        'prose max-w-none',
        'text-content-primary [&_*]:text-content-primary',
        '[&>ul]:space-y-4 [&>ul]:mb-6 [&>ul]:marker:text-content-primary',
        '[&>blockquote]:font-normal [&>blockquote]:bg-background-disabled [&>blockquote]:border-l-0 [&>blockquote>p]:m-0 [&>blockquote]:p-4 [&>blockquote]:rounded-xl [&>blockquote>p]:after:hidden [&>blockquote>p]:before:hidden [&>blockquote>p]:leading-5',
        '[&_img]:mx-auto [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:bg-background-disabled',
        className,
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: xss is handled
      dangerouslySetInnerHTML={{
        __html: filterXss(addImageOptimizations(content.value ?? '')),
      }}
    />
  );
};
